import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Stepper,
  Step,
  StepLabel,
  TextField,
  Button,
  Typography,
  Grid,
  Avatar,
  InputAdornment,
  IconButton,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Person,
  Email,
  Lock,
  CheckCircle,
  Phone,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { url } from '../constants';

const steps = ['Account Details', 'Select Preferences', 'Complete'];

const Signup = () => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [apiError, setApiError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    selectedCategories: [],
  });

  // Fetch categories on component mount
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await fetch(`${url}/users/get_categories`);
        if (!response.ok) throw new Error('Failed to fetch categories');
        const data = await response.json();
        setCategories(data);
      } catch (error) {
        console.error('Error:', error);
        setApiError('Failed to load categories. Please refresh the page.');
      } finally {
        setCategoriesLoading(false);
      }
    };
    fetchCategories();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (errors[name]) {
      setErrors({ ...errors, [name]: '' });
    }
  };

  // Real-time email validation
  const checkEmailExists = async (email) => {
    try {
      const response = await fetch(`${url}/users/check-email/${email}`);
      const data = await response.json();
      return data.exists;
    } catch (error) {
      console.error('Error checking email:', error);
      return false;
    }
  };

  // Real-time phone validation
  const checkPhoneExists = async (phone) => {
    try {
      const response = await fetch(`${url}/users/check-phone/${phone}`);
      const data = await response.json();
      return data.exists;
    } catch (error) {
      console.error('Error checking phone:', error);
      return false;
    }
  };

  const validateStep1 = async () => {
    const newErrors = {};

    // Full Name validation
    if (!formData.fullName.trim()) {
      newErrors.fullName = 'Full name is required';
    } else if (formData.fullName.trim().length < 3) {
      newErrors.fullName = 'Name must be at least 3 characters';
    }

    // Email validation
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    } else {
      try {
        const emailExists = await checkEmailExists(formData.email);
        if (emailExists) {
          newErrors.email = 'This email is already registered';
        }
      } catch (error) {
        console.error('Error checking email:', error);
        newErrors.email = 'Error validating email. Please try again.';
      }
    }

    // Phone validation
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^[6-9][0-9]{9}$/.test(formData.phone)) {
      newErrors.phone = 'Please enter a valid 10-digit Indian mobile number';
    } else {
      try {
        const phoneExists = await checkPhoneExists(formData.phone);
        if (phoneExists) {
          newErrors.phone = 'This phone number is already registered';
        }
      } catch (error) {
        console.error('Error checking phone:', error);
        newErrors.phone = 'Error validating phone. Please try again.';
      }
    }

    // Password validation
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 4) {
      newErrors.password = 'Password must be at least 4 characters';
    }

    // Confirm password validation
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep2 = () => {
    if (formData.selectedCategories.length === 0) {
      setErrors({ categories: 'Please select at least one category of interest' });
      return false;
    }
    setErrors({});
    return true;
  };

  const handleNext = async () => {
    if (activeStep === 0) {
      setLoading(true);
      try {
        const isValid = await validateStep1();
        setLoading(false);
        if (!isValid) {
          return;
        }
      } catch (error) {
        console.error('Validation error:', error);
        setLoading(false);
        return;
      }
    }

    if (activeStep === 1) {
      if (!validateStep2()) {
        return;
      }
    }

    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
    setApiError('');
    setSuccessMessage('');
  };
  
  const handleSubmit = async () => {
    setLoading(true);
    setApiError('');
    setSuccessMessage('');
  
    const signupData = {
      full_name: formData.fullName.trim(),
      email: formData.email.trim().toLowerCase(),
      phone: formData.phone.trim(),
      password: formData.password,
      preferences: formData.selectedCategories,
      preference_count: formData.selectedCategories.length,
    };
  
    console.log('Final preferences:', signupData.preferences);
  
    try {
      const response = await fetch(`${url}/users/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(signupData),
      });
  
      const data = await response.json();
  
      if (!response.ok) {
        throw new Error(data.detail || 'Registration failed');
      }
  
      setSuccessMessage('✅ Account created successfully! Redirecting to login...');
      setTimeout(() => {
        navigate('/login', {
          state: { message: 'Account created! Please login with your credentials.' }
        });
      }, 2000);
    } catch (error) {
      console.error('Signup error:', error);
      setApiError(error.message || 'Failed to create account. Please try again.');
      setLoading(false);
    }
  };

  const renderStep1 = () => (
    <Box>
      <Typography variant="h5" gutterBottom fontWeight={600} sx={{ mb: 1 }}>
        Create Your Account
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
        Join SeekDeals to discover amazing local deals and offers
      </Typography>

      {apiError && <Alert severity="error" sx={{ mb: 3 }}>{apiError}</Alert>}

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Full Name"
            name="fullName"
            value={formData.fullName}
            onChange={handleChange}
            error={!!errors.fullName}
            helperText={errors.fullName}
            placeholder="Enter your full name"
            InputProps={{
              startAdornment: <InputAdornment position="start"><Person /></InputAdornment>,
            }}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Email Address"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            error={!!errors.email}
            helperText={errors.email}
            placeholder="your.email@example.com"
            InputProps={{
              startAdornment: <InputAdornment position="start"><Email /></InputAdornment>,
            }}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Phone Number"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            error={!!errors.phone}
            helperText={errors.phone}
            placeholder="10-digit mobile number (e.g., 9876543210)"
            InputProps={{
              startAdornment: <InputAdornment position="start"><Phone /></InputAdornment>,
            }}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Password"
            name="password"
            type={showPassword ? 'text' : 'password'}
            value={formData.password}
            onChange={handleChange}
            error={!!errors.password}
            helperText={errors.password}
            placeholder="Min 4 characters"
            InputProps={{
              startAdornment: <InputAdornment position="start"><Lock /></InputAdornment>,
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Confirm Password"
            name="confirmPassword"
            type="password"
            value={formData.confirmPassword}
            onChange={handleChange}
            error={!!errors.confirmPassword}
            helperText={errors.confirmPassword}
            placeholder="Re-enter your password"
          />
        </Grid>
      </Grid>

      <Box sx={{ mt: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          Already have an account?{' '}
          <Button
            onClick={() => navigate('/login')}
            sx={{ textTransform: 'none', fontWeight: 600, color: '#1a7f3a' }}
          >
            Login here
          </Button>
        </Typography>
      </Box>
    </Box>
  );

  const renderStep2 = () => (
    <Box>
      <Typography variant="h5" gutterBottom fontWeight={600} sx={{ mb: 1 }}>
        Select Your Interests
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Choose categories you're interested in. We'll show you personalized deals based on your preferences.
      </Typography>
  
      {errors.categories && <Alert severity="warning" sx={{ mb: 3 }}>{errors.categories}</Alert>}
  
      <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
        Selected: <strong>{formData.selectedCategories.length}</strong> categories
      </Typography>
  
      {categoriesLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : categories.length === 0 ? (
        <Alert severity="info">No categories available at the moment.</Alert>
      ) : (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          {categories.map((category) => {
            const categoryId = category.cate_id;
            const isSelected = formData.selectedCategories.includes(categoryId);
  
            return (
              <Button
                key={categoryId}
                onClick={() => {
                  if (isSelected) {
                    setFormData({
                      ...formData,
                      selectedCategories: formData.selectedCategories.filter(
                        (id) => id !== categoryId
                      ),
                    });
                  } else {
                    setFormData({
                      ...formData,
                      selectedCategories: [...formData.selectedCategories, categoryId],
                    });
                  }
                }}
                variant={isSelected ? 'contained' : 'outlined'}
                sx={{
                  borderRadius: '20px',
                  px: 3,
                  py: 1.5,
                  bgcolor: isSelected ? category.cate_color : 'transparent',
                  borderColor: isSelected ? category.cate_color : '#ddd',
                  borderWidth: 2,
                  color: isSelected ? 'white' : 'text.primary',
                  fontWeight: 600,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    bgcolor: isSelected ? category.cate_color : 'action.hover',
                    transform: 'scale(1.05)',
                  },
                }}
              >
                {category.cate_name}
              </Button>
            );
          })}
        </Box>
      )}
    </Box>
  );

  const renderStep3 = () => (
    <Box sx={{ textAlign: 'center', py: 4 }}>
      <Avatar sx={{ width: 80, height: 80, bgcolor: 'success.main', mx: 'auto', mb: 3 }}>
        <CheckCircle sx={{ fontSize: 50 }} />
      </Avatar>
      <Typography variant="h4" gutterBottom fontWeight={700}>
        Welcome to SeekDeals!
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
        Your account setup is almost complete.
      </Typography>

      {successMessage && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {successMessage}
        </Alert>
      )}

      {formData.selectedCategories.length > 0 && (
        <Paper
          elevation={0}
          sx={{
            p: 3,
            bgcolor: 'background.default',
            borderRadius: 2,
            mb: 4,
          }}
        >
          <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
            Your Selected {formData.selectedCategories.length} Categories:
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, justifyContent: 'center' }}>
            {formData.selectedCategories.map((catId) => {
              const cat = categories.find((c) => c.cate_id === catId || c._id === catId);
              return (
                <Paper
                  key={catId}
                  sx={{
                    px: 2.5,
                    py: 1.2,
                    bgcolor: cat?.cate_color || '#999',
                    color: 'white',
                    borderRadius: '20px',
                    fontSize: '0.9rem',
                    fontWeight: 600,
                  }}
                >
                  {cat?.cate_name || 'Unknown'}
                </Paper>
              );
            })}
          </Box>
        </Paper>
      )}

      <Button
        variant="contained"
        onClick={handleSubmit}
        disabled={loading}
        size="large"
        sx={{ px: 5, py: 1.5, mt: 2 }}
      >
        {loading ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={20} color="inherit" />
            Creating Account...
          </Box>
        ) : 'Create Account'}
      </Button>
    </Box>
  );

  return (
    <>
      <Box
        sx={{
          minHeight: '100vh',
          position: 'relative',
          py: 6,
          background: `
            linear-gradient(135deg, #0f5328 0%, #1a7f3a 25%, #2d9e4a 50%, #4caf50 75%, #66bb6a 100%),
            radial-gradient(circle at 15% 25%, rgba(255,255,255,0.25) 0%, transparent 50%),
            radial-gradient(circle at 85% 65%, rgba(255,255,255,0.2) 0%, transparent 50%),
            radial-gradient(ellipse at 35% 15%, rgba(76,175,80,0.3) 0%, transparent 60%),
            radial-gradient(circle at 65% 85%, rgba(26,127,58,0.25) 0%, transparent 50%)
          `,
          backgroundSize: '400% 400%, 250px 250px, 350px 350px, 300px 200px, 220px 220px',
          animation: 'heroShift 6s ease infinite',
          overflow: 'hidden',
        }}
      >
        {/* Green Circle */}
        <Box
          sx={{
            position: 'absolute',
            top: '10%',
            left: '5%',
            width: 180,
            height: 180,
            background: 'radial-gradient(circle, rgba(76,175,80,0.5) 0%, rgba(26,127,58,0.2) 70%)',
            borderRadius: '50%',
            animation: 'float1 18s infinite linear',
            boxShadow: '0 0 40px rgba(76,175,80,0.6)',
            zIndex: 1,
          }}
        />
        
        {/* Golden Circle - NEW */}
        <Box
          sx={{
            position: 'absolute',
            top: '15%',
            right: '10%',
            width: 140,
            height: 140,
            background: 'radial-gradient(circle, rgba(255,215,0,0.6) 0%, rgba(255,193,7,0.3) 70%)',
            borderRadius: '50%',
            animation: 'float2 14s infinite linear',
            boxShadow: '0 0 40px rgba(255,215,0,0.7)',
            zIndex: 1,
          }}
        />
        
        {/* White Circle */}
        <Box
          sx={{
            position: 'absolute',
            bottom: '15%',
            right: '8%',
            width: 160,
            height: 160,
            background: 'radial-gradient(circle, rgba(255,255,255,0.4) 0%, rgba(255,255,255,0.1) 70%)',
            borderRadius: '50%',
            animation: 'float3 16s infinite linear',
            boxShadow: '0 0 30px rgba(255,255,255,0.5)',
            zIndex: 1,
          }}
        />
        
        {/* Golden Triangle - NEW */}
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '8%',
            width: 120,
            height: 120,
            background: 'linear-gradient(45deg, rgba(255,193,7,0.5) 0%, rgba(255,215,0,0.35) 100%)',
            clipPath: 'polygon(50% 0%, 0% 100%, 100% 100%)',
            animation: 'float4 12s infinite linear',
            boxShadow: '0 0 30px rgba(255,215,0,0.6)',
            zIndex: 1,
          }}
        />
        
        {/* Green Oval */}
        <Box
          sx={{
            position: 'absolute',
            bottom: '25%',
            left: '15%',
            width: 200,
            height: 90,
            background: 'linear-gradient(90deg, rgba(26,127,58,0.4) 0%, rgba(46,158,74,0.5) 50%, rgba(102,187,106,0.3) 100%)',
            borderRadius: '50px',
            animation: 'float5 22s infinite linear',
            boxShadow: '0 0 35px rgba(26,127,58,0.5)',
            zIndex: 1,
          }}
        />

        {/* Golden Square - NEW */}
        <Box
          sx={{
            position: 'absolute',
            top: '70%',
            right: '20%',
            width: 110,
            height: 110,
            background: 'linear-gradient(135deg, rgba(255,215,0,0.5) 0%, rgba(255,193,7,0.4) 100%)',
            borderRadius: '15px',
            animation: 'float1 20s infinite linear',
            boxShadow: '0 0 30px rgba(255,215,0,0.6)',
            zIndex: 1,
            transform: 'rotate(25deg)',
          }}
        />

        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 10 }}>
          <Paper 
            elevation={0}
            sx={{ 
              p: { xs: 2, sm: 4 }, 
              borderRadius: 3,
              bgcolor: 'rgba(255,255,255,0.95)',
              backdropFilter: 'blur(20px)',
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
              border: '1px solid rgba(255,255,255,0.3)',
            }}
          >
            <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>

            <Box sx={{ minHeight: 400 }}>
              {activeStep === 0 && renderStep1()}
              {activeStep === 1 && renderStep2()}
              {activeStep === 2 && renderStep3()}
            </Box>

            {activeStep < 2 && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4, pt: 3, borderTop: 1, borderColor: 'divider' }}>
                <Button 
                  disabled={activeStep === 0 || loading} 
                  onClick={handleBack} 
                  variant="outlined" 
                  size="large"
                >
                  Back
                </Button>
                <Button 
                  variant="contained" 
                  onClick={handleNext} 
                  size="large" 
                  disabled={loading}
                >
                  {loading ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CircularProgress size={20} color="inherit" />
                      Validating...
                    </Box>
                  ) : activeStep === 1 ? 'Finish' : 'Next'}
                </Button>
              </Box>
            )}
          </Paper>
        </Container>
      </Box>

      {/* Animations */}
      <style jsx>{`
        @keyframes heroShift {
          0%, 100% { background-position: 0% 50%, 20% 30%, 85% 65%, 35% 15%, 65% 85%; }
          25% { background-position: 100% 50%, 100% 100%, 15% 100%, 85% 50%, 35% 0%; }
          50% { background-position: 50% 100%, 0% 0%, 100% 100%, 0% 50%, 100% 50%; }
          75% { background-position: 0% 50%, 0% 100%, 85% 0%, 35% 100%, 65% 50%; }
        }
        
        @keyframes float1 {
          0%, 100% { transform: translate(0, 0) rotate(0deg) scale(1); }
          25% { transform: translate(40px, -40px) rotate(90deg) scale(1.1); }
          50% { transform: translate(-20px, 30px) rotate(180deg) scale(0.95); }
          75% { transform: translate(25px, -25px) rotate(270deg) scale(1.05); }
        }
        
        @keyframes float2 {
          0%, 100% { transform: translate(0, 0) rotate(0deg); }
          33% { transform: translate(-50px, 40px) rotate(-120deg); }
          66% { transform: translate(35px, -35px) rotate(-240deg); }
        }
        
        @keyframes float3 {
          0%, 100% { transform: translate(0, 0) rotate(0deg) scale(1); }
          33% { transform: translate(-30px, 25px) rotate(120deg) scale(1.15); }
          66% { transform: translate(20px, -20px) rotate(240deg) scale(0.9); }
        }
        
        @keyframes float4 {
          0%, 100% { transform: translate(0, 0) rotate(0deg); }
          50% { transform: translate(-25px, 25px) rotate(180deg); }
        }
        
        @keyframes float5 {
          0%, 100% { transform: translate(0, 0) scale(1); }
          33% { transform: translate(30px, -20px) scale(1.1); }
          66% { transform: translate(-25px, 20px) scale(0.95); }
        }
      `}</style>
    </>
  );
};

export default Signup;
